import React from "react";
import { t } from "ttag";

import { FormRadioSelect } from "../../../forms/FormRadioSelect";
import { FormSelect } from "../../../forms/FormSelect";
import {
    IFormUUID,
    IReviewsProductTypeID,
    isoReviewsProductID,
    isoReviewsProductTypeID,
} from "../../../models/nominals";
import {
    IReviewsProduct,
    IReviewsProductVariantChoices,
} from "../../../models/reviews";
import { useAppDispatch, useAppSelector } from "../../reducers";
import { NOT_SURE_VARIANT_CHOICE_SLUG } from "../constants";
import { reviewFormVariantChange } from "../reducers";
import {
    getWriteReviewFormProducts,
    getWriteReviewFormSelectedProductID,
    getWriteReviewFormSelectedProductTypeID,
    getWriteReviewFormSelectedVariantSlug,
    getWriteReviewFormState,
    getWriteReviewFormVariantChoices,
} from "../selectors";
import { loadReviewTemplate, loadReviewTemplateByProductType } from "../thunks";

interface IChoice {
    label: string;
    value: string;
}

const getProductTypeChoices = (
    products: ReadonlyArray<IReviewsProduct>,
    formUUID: IFormUUID,
): IChoice[] => {
    if (!products) {
        return [];
    }
    const productTypeIDs: { [i: string]: IReviewsProductTypeID } = {};
    const productTypeSortOrders: { [i: string]: number } = {};
    const productTypeNames: string[] = [];
    products.forEach((product) => {
        if (product.product_type && product.product_type_name) {
            productTypeIDs[product.product_type_name] = product.product_type;
            productTypeSortOrders[product.product_type_name] =
                product.product_type_sort_order;
            if (productTypeNames.indexOf(product.product_type_name) === -1) {
                productTypeNames.push(product.product_type_name);
            }
        }
    }, []);
    productTypeNames.sort((a, b) => {
        const sortA = productTypeSortOrders[a];
        const sortB = productTypeSortOrders[b];
        if (sortA === -1) {
            return 1;
        }
        if (sortB === -1) {
            return -1;
        }
        if (sortA === sortB) {
            return 0;
        }
        return sortA > sortB ? 1 : -1;
    });
    const productChoices = productTypeNames.map((typeName) => {
        return {
            label: typeName,
            value: `${formUUID}:product-type:${productTypeIDs[typeName]}`,
        };
    });
    return productChoices;
};

const getProductChoices = (
    products: ReadonlyArray<IReviewsProduct>,
    selectedProductTypeID: IReviewsProductTypeID | null,
): IChoice[] => {
    if (!selectedProductTypeID || products.length <= 0) {
        return [];
    }
    const productChoices = products
        .filter((product) => {
            return product.product_type === selectedProductTypeID;
        })
        .map((product) => {
            return {
                label: product.name,
                value: `product:${product.id}`,
            };
        });
    return productChoices;
};

const getVariantChoices = (
    variantChoices: IReviewsProductVariantChoices[],
    selectedProductTypeID: IReviewsProductTypeID | null,
    products: ReadonlyArray<IReviewsProduct>,
): IChoice[] => {
    if (
        !selectedProductTypeID ||
        products.length <= 0 ||
        variantChoices.length <= 0
    ) {
        return [];
    }
    const choices = variantChoices.map((variant) => {
        if (variant === NOT_SURE_VARIANT_CHOICE_SLUG) {
            return {
                label: t`I'm not sure`,
                value: NOT_SURE_VARIANT_CHOICE_SLUG,
            };
        } else if (variant) {
            return {
                label: variant.display_name,
                value: variant.slug,
            };
        } else {
            return {
                label: t`--- select model ---`,
                value: "",
            };
        }
    });
    return choices;
};

export const WriteReviewFormProductSelect = (props: {
    formUUID: IFormUUID;
    displayedProductTypeIDWhitelist?: IReviewsProductTypeID[];
}): React.ReactNode => {
    const dispatch = useAppDispatch();

    const products = useAppSelector((rootState) =>
        getWriteReviewFormProducts(rootState.reviews, props),
    );
    const formState = useAppSelector((rootState) =>
        getWriteReviewFormState(rootState.reviews, props),
    );
    const variantChoices = useAppSelector((rootState) =>
        getWriteReviewFormVariantChoices(rootState.reviews, props),
    );
    const selectedProductTypeID = useAppSelector((rootState) =>
        getWriteReviewFormSelectedProductTypeID(rootState.reviews, props),
    );
    const selectedProductID = useAppSelector((rootState) =>
        getWriteReviewFormSelectedProductID(rootState.reviews, props),
    );
    const selectedVariantSlug = useAppSelector((rootState) =>
        getWriteReviewFormSelectedVariantSlug(rootState.reviews, props),
    );

    const onTypeChange = (e: React.FormEvent<HTMLInputElement>) => {
        if (!e.currentTarget.checked) {
            return;
        }
        e.preventDefault();
        const productTypeID = isoReviewsProductTypeID.wrap(
            parseInt(e.currentTarget.value.split(":")[2], 10),
        );
        void dispatch(
            loadReviewTemplateByProductType({
                formUUID: props.formUUID,
                products,
                productTypeID,
            }),
        );
    };

    const onProductChange = (e: React.FormEvent<HTMLSelectElement>) => {
        const productID = isoReviewsProductID.wrap(
            parseInt(e.currentTarget.value.split(":")[1], 10),
        );
        void dispatch(
            loadReviewTemplate({
                formUUID: props.formUUID,
                productID,
            }),
        );
    };

    const onVariantChange = (e: React.FormEvent<HTMLSelectElement>) => {
        dispatch(
            reviewFormVariantChange({
                formUUID: props.formUUID,
                selectedVariant: e.currentTarget.value,
            }),
        );
    };

    if (!products) {
        return null;
    }

    const productTypeChoices = getProductTypeChoices(products, props.formUUID);
    const productChoices = getProductChoices(products, selectedProductTypeID);
    const variantChoicesArray = getVariantChoices(
        variantChoices,
        selectedProductTypeID,
        products,
    );

    let productTypeSelect: React.JSX.Element | null = null;
    let productSelect: React.JSX.Element | null = null;
    let variantSelect: React.JSX.Element | null = null;
    let productSelectError: React.JSX.Element | null = null;
    let variantSelectError: React.JSX.Element | null = null;

    if (productTypeChoices.length > 1) {
        productTypeSelect = (
            <FormRadioSelect
                name={`${props.formUUID}__product-type-id`}
                required={true}
                label={t`Which type of product?`}
                labelPlacement="static"
                choices={productTypeChoices}
                onChange={onTypeChange}
                value={`${props.formUUID}:product-type:${selectedProductTypeID}`}
            />
        );
        if (formState.showProductSelectError) {
            productSelectError = (
                <div className="wrf-product-select-form__error">
                    {t`Please select a product type`}
                </div>
            );
        }
    }
    if (selectedProductID) {
        productSelect = (
            <FormSelect
                name={`${props.formUUID}__product-id`}
                id={`${props.formUUID}__product-id`}
                required={true}
                label={t`Which product do you own?`}
                labelPlacement="static"
                choices={productChoices}
                onChange={onProductChange}
                value={`product:${selectedProductID}`}
            />
        );
    }
    if (variantChoicesArray.length > 1) {
        // variantChoices always has one "not sure" choice
        variantSelect = (
            <FormSelect
                name={`${props.formUUID}__variant`}
                id={`${props.formUUID}__variant`}
                required={true}
                label={t`Which model do you own?`}
                labelPlacement="static"
                choices={variantChoicesArray}
                onChange={onVariantChange}
                value={selectedVariantSlug}
            />
        );
        if (formState.showVariantSelectError) {
            variantSelectError = (
                <div className="wrf-product-select-form__error">
                    {t`Please select a model`}
                </div>
            );
        }
    }

    return (
        <div className="wrf-product-select-form">
            {productTypeSelect}
            {productSelectError}
            {productSelect}
            {variantSelect}
            {variantSelectError}
        </div>
    );
};
